import { Button, SimpleAlert, StandardAlert } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateInvitations } from '../../queries/use-create-invitation';
import { UserRoleEnum, UserTemplateCsv } from '../../types/user';
import { SubscriptionTier } from '../../types/firm';
import { useNavigate } from 'react-router-dom';
import NewUserRow from './NewUserRow';
import Papa from 'papaparse';
import TemplateUploadDropzone from '../../components/file-upload/TemplateUploadDropzone';
import TemplateUploadPreview from '../../components/file-upload/TemplateUploadPreview';
import VerticalRadioInput from '../../components/radio-input/VerticalRadioInput';
import UpgradePrompt from '../../components/upgrade-prompt/UpgradePrompt';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';

export type NewUserRowConfig = {
    id: string;
    email: string;
    role: UserRoleEnum;
    errorMessage?: string;
};

type NewUserFormProps = { invites?: NewUserRowConfig[]; showImportSelect?: boolean };

function NewUserForm({ invites: _invites, showImportSelect = true }: NewUserFormProps): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const [rowIdCounter, setRowIdCounter] = useState(1);
    const [invites, setInvites] = useState<NewUserRowConfig[]>(_invites || [{ id: 'row-1', email: '', role: UserRoleEnum.USER }]);
    const [successfulInvites, setSuccessfulInvites] = useState(0);
    const [input, setInput] = useState('individual');
    const [file, setFile] = useState<string | undefined>();
    const [fileError, setFileError] = useState<string | undefined>(undefined);
    const [rowData, setRowData] = useState<UserTemplateCsv[] | undefined>();
    const [promptUpgrade, setPromptUpgrade] = useState(false);
    const featureAccess = { canBulkImportUsers: useHasFeatureAccess('user-bulk-import:write') };

    const createInvitationsQuery = useCreateInvitations();
    const columnFields = ['Email *', 'Role *'];

    function handleRedirect(): void {
        navigate(`/${shortId}/settings?tab=invitations`);
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (input === 'bulk' && !rowData) {
            setFileError('A file must be uploaded.');
            return;
        }

        if (fileError) {
            return;
        }

        const allInvites = [];
        let hasError = false;
        for (const invite of invites) {
            if (invite.email === '') {
                allInvites.push({ ...invite, errorMessage: 'A user must have an email.' });
                hasError = true;
                continue;
            }
            if (!/.+@.+\..+\.*.*[^.]$/gim.test(invite.email)) {
                allInvites.push({ ...invite, errorMessage: 'Please enter a valid email address.' });
                hasError = true;
                continue;
            }
            allInvites.push(invite);
        }

        if (hasError) {
            setInvites(allInvites);
            return;
        }

        createInvitationsQuery.mutate(
            {
                invites: invites.map((invite) => ({ email: invite.email, role: invite.role })),
            },
            {
                onSuccess: () => {
                    handleRedirect();
                    toastNotification('success', `${invites.length} new ${invites.length === 1 ? 'user' : 'users'} invited!`);
                },
                onError: (err) => {
                    // Any successful ones show an alert
                    const successInvites = err.response?.data.results.filter((r: { statusCode: number }) => r.statusCode === 201);
                    const errorInvites = err.response?.data.results.filter((r: { statusCode: number }) => r.statusCode !== 201);

                    const usedRowIds: string[] = [];
                    const inviteRows: NewUserRowConfig[] = [];

                    let successes = 0;

                    for (const inviteRow of invites) {
                        const isSuccessRow = successInvites?.find(
                            (s: { data: { email: string; role: UserRoleEnum } }) =>
                                s.data.email === inviteRow.email && s.data.role === inviteRow.role
                        );

                        if (!isSuccessRow) {
                            // Error row
                            const errorRow = errorInvites.find(
                                (e: { error: { original: { email: string; role: UserRoleEnum } } }) =>
                                    e.error.original.email === inviteRow.email &&
                                    e.error.original.role === inviteRow.role &&
                                    !usedRowIds.includes(inviteRow.id)
                            );
                            inviteRows.push({ ...inviteRow, errorMessage: errorRow.error.fields.email });
                        } else {
                            successes++;
                            const index = successInvites.indexOf(isSuccessRow);
                            successInvites.splice(index, 1);
                        }

                        usedRowIds.push(inviteRow.id);
                    }

                    setInvites(inviteRows);
                    setSuccessfulInvites(successes);
                    toastNotification('error', `Unable to invite ${errorInvites.length} ${errorInvites.length === 1 ? 'user' : 'users'}!`);
                },
            }
        );
    }

    function addRow(): void {
        setInvites((prev) => [...prev, { id: 'row-' + (rowIdCounter + 1), email: '', role: UserRoleEnum.USER }]);
        setRowIdCounter((prev) => prev + 1);
        setSuccessfulInvites(0);
    }

    function deleteRow(id: string): void {
        if (invites.length === 1) {
            return;
        }
        setSuccessfulInvites(0);
        setInvites((prev) => [...prev.filter((row) => row.id !== id)]);
    }

    function handleEmailChange(id: string, email: string): void {
        setSuccessfulInvites(0);
        setInvites((prev) => {
            return prev.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                return {
                    ...row,
                    email,
                    errorMessage: undefined,
                };
            });
        });
    }

    function handleRoleChange(id: string, role: UserRoleEnum): void {
        setSuccessfulInvites(0);
        setInvites((prev) => {
            return prev.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                return { ...row, role, errorMessage: undefined };
            });
        });
    }

    function deleteFile(): void {
        setFile(undefined);
        setFileError(undefined);
        setRowData(undefined);
        setSuccessfulInvites(0);
        setInvites([{ id: 'row-1', email: '', role: UserRoleEnum.USER }]);
        setRowIdCounter(1);
    }

    function handleInputChange(input: string): void {
        if (input === 'individual') {
            deleteFile();
        }

        if (!featureAccess.canBulkImportUsers && input === 'bulk') {
            setPromptUpgrade(true);
            setInput('individual');
            return;
        }

        setInput(input);
    }

    function formatData(rows: UserTemplateCsv[]): NewUserRowConfig[] {
        const inviteRows: NewUserRowConfig[] = [];

        for (let i = 0; i < rows.length; i++) {
            let role = UserRoleEnum.USER;

            if (rows[i]['Email *'] === '' || !rows[i]['Email *']) {
                continue;
            }

            if (['USER', 'MANAGER', 'EXTERNAL_USER'].includes(rows[i]['Role *']?.trim()?.toUpperCase())) {
                role = rows[i]['Role *'].trim().toUpperCase() as UserRoleEnum;
            }
            inviteRows.push({ id: `row-${i + 1}`, email: rows[i]['Email *'], role });
        }

        return inviteRows;
    }

    function upload(files: File[]): void {
        setFile(files[0].name);
        setFileError(undefined);

        if (files[0].type !== 'text/csv') {
            setFileError('Invalid file type. File must be .csv extension.');
            return;
        }

        Papa.parse(files[0], {
            header: true,
            complete: function (results) {
                const data = results.data as UserTemplateCsv[];
                setRowData(data);

                if (data.length === 0) {
                    setFileError('File must contains at least 2 rows of data.');
                    return;
                }
                if (JSON.stringify(Object.getOwnPropertyNames(data[0])) !== JSON.stringify(columnFields)) {
                    setFileError('Invalid file. The template should be used.');
                    return;
                }

                const inviteRows = formatData(data);
                setInvites(inviteRows);
                setRowIdCounter(inviteRows.length);
            },
        });
    }

    return (
        <>
            {successfulInvites > 0 && (
                <div className="mb-4">
                    <SimpleAlert
                        type="success"
                        title={`Successfully invited ${successfulInvites} ${successfulInvites > 1 ? 'users' : 'user'}`}
                    />
                </div>
            )}

            {promptUpgrade && <UpgradePrompt onClose={(): void => setPromptUpgrade(false)} targetTier={SubscriptionTier.ESSENTIAL} />}

            <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
                {fileError && (
                    <div className="mb-4">
                        <SimpleAlert type="error" title={fileError} />
                    </div>
                )}

                {/* INDIVIDUAL OR BULK INPUT */}
                {showImportSelect && (
                    <VerticalRadioInput
                        title="Import type"
                        description="How would you like to add your user?"
                        optionOneLabel="Individual"
                        optionTwoLabel="Bulk"
                        isDisabledOptionTwo={createInvitationsQuery.isLoading}
                        isDisabledOptionOne={createInvitationsQuery.isLoading}
                        value={input}
                        onValueChange={handleInputChange}
                    />
                )}

                {input === 'bulk' && !file && (
                    <StandardAlert
                        type="info"
                        title="Ensure the following"
                        description={['Email addresses must be unique.', 'User role must be either USER, MANAGER or EXTERNAL_USER.']}
                    >
                        <div></div>
                    </StandardAlert>
                )}
                {/* FILE UPLOAD SECTION */}
                {input === 'bulk' && !file && (
                    <TemplateUploadDropzone columns={['"Email *","Role *"']} fileName="invitations.csv" onUpload={upload} />
                )}

                {/* SHOW UPLOADED FILE NAME */}
                {file && <TemplateUploadPreview fileName={file} onDelete={deleteFile} />}

                {/* INDIVIDUAL INPUT ROW / PREFILLED ROWS FROM BULK */}
                {(input === 'individual' || (file && !fileError)) && (
                    <div className="flex flex-col gap-2 max-h-[650px] overflow-y-auto p-1">
                        {invites.map((invite) => (
                            <NewUserRow
                                key={invite.id}
                                id={invite.id}
                                email={invite.email}
                                role={invite.role}
                                errorMessage={invite.errorMessage}
                                onEmailChange={handleEmailChange}
                                onDelete={deleteRow}
                                onRoleChange={handleRoleChange}
                                canRemove={invites.length > 1}
                                isSubmitting={createInvitationsQuery.isLoading}
                            />
                        ))}
                        <Button type="hollow" label="Add another" onClick={addRow} disabled={createInvitationsQuery.isLoading} />
                    </div>
                )}

                {/* FORM CANCEL + SUBMIT */}
                <div className="flex justify-end gap-4">
                    <Button type="hollow" label="Cancel" onClick={handleRedirect} disabled={createInvitationsQuery.isLoading} />
                    <Button
                        type="primary"
                        label={createInvitationsQuery.isLoading ? 'Inviting...' : 'Invite'}
                        disabled={createInvitationsQuery.isLoading}
                        isLoading={createInvitationsQuery.isLoading}
                        isSubmit
                    />
                </div>
            </form>
        </>
    );
}
export default NewUserForm;
